import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

// ── Locale names map ──────────────────────────────────────────────────────────
export const LOCALE_NAMES: Record<string, string> = {
  en: 'English',
  kn: 'ಕನ್ನಡ',
  hi: 'हिन्दी',
  ta: 'தமிழ்',
  te: 'తెలుగు',
  mr: 'मराठी',
  ml: 'മലയാളം',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español',
  ja: '日本語',
  zh: '中文',
};

const STORAGE_KEY = 'jslipi-locale';
const DEFAULT_LOCALE = 'en';

interface LocaleContextValue {
  locale: string;
  setLocale: (l: string) => void;
  getLocaleName: (code: string) => string;
  /** Locales available for the currently viewed content (set by PostPage/BlogReaderLayout) */
  availableLocales: string[];
  setAvailableLocales: (locales: string[]) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<string>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || DEFAULT_LOCALE;
    } catch {
      return DEFAULT_LOCALE;
    }
  });
  const [availableLocales, setAvailableLocales] = useState<string[]>([]);

  const setLocale = useCallback((l: string) => {
    setLocaleState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      // ignore
    }
  }, []);

  // Sync with system language on first visit (if no stored preference)
  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        const browserLang = navigator.language?.split('-')[0] || DEFAULT_LOCALE;
        // Only auto-set if we have a name for this locale (supported)
        if (LOCALE_NAMES[browserLang]) {
          setLocale(browserLang);
        }
      }
    } catch {
      // ignore
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getLocaleName = useCallback(
    (code: string) => LOCALE_NAMES[code] || code.toUpperCase(),
    []
  );

  return (
    <LocaleContext.Provider value={{ locale, setLocale, getLocaleName, availableLocales, setAvailableLocales }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside <LocaleProvider>');
  return ctx;
}
